import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Users, MapPin, Check, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { packages } from '../data/packages';
import { Reveal } from '../components/Reveal';

const PrivateTrips = () => {
  const { lang, t } = useLanguage();

  return (
    <div className="bg-ivory" data-testid="private-trips-page">
      <section className="mx-auto max-w-7xl px-5 pb-24 pt-36 md:px-8 md:pt-44">
        <Reveal>
          <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold-dark">{t.trips.kicker}</p>
          <h1 className="mt-4 max-w-3xl font-heading text-4xl font-medium tracking-tight text-navy md:text-6xl">{t.trips.title}</h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-navy/60">{t.trips.sub}</p>
        </Reveal>

        <div className="mt-16 flex flex-col gap-16">
          {packages.map((pkg, i) => (
            <Reveal key={pkg.id} delay={0.05}>
              <article
                className={`grid overflow-hidden border border-navy/10 bg-white lg:grid-cols-5 ${i % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}
                data-testid={`package-card-${pkg.id}`}
              >
                <div className="relative min-h-[18rem] overflow-hidden lg:col-span-2 lg:[direction:ltr]">
                  <img
                    src={pkg.image}
                    alt={pkg.name[lang]}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>

                <div className="p-8 md:p-12 lg:col-span-3 lg:[direction:ltr]">
                  <h2 className="font-heading text-2xl font-medium tracking-tight text-navy md:text-3xl">{pkg.name[lang]}</h2>

                  <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3 text-xs text-navy/60">
                    <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold-dark" /> {pkg.destination[lang]}</span>
                    <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold-dark" /> {t.trips.duration}: {pkg.duration[lang]}</span>
                    <span className="flex items-center gap-2"><Users className="h-4 w-4 text-gold-dark" /> {t.trips.groupSize}: {pkg.groupSize[lang]}</span>
                  </div>

                  <div className="mt-8 grid gap-8 md:grid-cols-2">
                    <div>
                      <h3 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold-dark">{t.trips.itinerary}</h3>
                      <ul className="mt-4 flex flex-col gap-3">
                        {pkg.itinerary.map((item, j) => (
                          <li key={j} className="text-sm leading-relaxed text-navy/70">{item[lang]}</li>
                        ))}
                      </ul>
                      <h3 className="mt-6 text-[11px] font-semibold uppercase tracking-[0.3em] text-gold-dark">{t.trips.attractions}</h3>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {pkg.attractions.map((a, j) => (
                          <span key={j} className="border border-navy/15 px-3 py-1.5 text-xs text-navy/70">{a[lang]}</span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold-dark">{t.trips.included}</h3>
                      <ul className="mt-4 flex flex-col gap-2.5">
                        {pkg.included.map((item, j) => (
                          <li key={j} className="flex items-start gap-2.5 text-sm text-navy/70">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" /> {item[lang]}
                          </li>
                        ))}
                      </ul>
                      <h3 className="mt-6 text-[11px] font-semibold uppercase tracking-[0.3em] text-navy/40">{t.trips.excluded}</h3>
                      <ul className="mt-4 flex flex-col gap-2.5">
                        {pkg.excluded.map((item, j) => (
                          <li key={j} className="flex items-start gap-2.5 text-sm text-navy/45">
                            <X className="mt-0.5 h-4 w-4 shrink-0" /> {item[lang]}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <Link
                    to={`/plan?package=${pkg.id}&destination=${pkg.destinationSlug}`}
                    data-testid={`request-trip-${pkg.id}`}
                    className="group mt-10 inline-flex items-center gap-3 bg-navy px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-gold hover:text-navy"
                  >
                    {t.trips.request}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 border-l-2 border-gold bg-beige p-6">
          <p className="text-sm italic leading-relaxed text-navy/70" data-testid="trips-note">{t.trips.note}</p>
        </Reveal>
      </section>
    </div>
  );
};

export default PrivateTrips;
